/**
 * AI CONTEXT
 * Purpose: session card summary (time/class/project/teachers/headcount/status chips).
 * Depends on: AdminViewContext maps + attendance summarizers.
 * Used by: AttendanceView and TeacherSchedule.
 * Importance: high-density session status snapshot in operations views.
 * Refactor: split IconsRow and teacher-name rendering into smaller presentational components.
 */
import { CheckCircleOutlined, UserOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import { useMemo, useState } from "react"

import MakeupsIcon from "#assets/makeupsIcon.svg?react"
import PlannedAbsencesIcon from "#assets/plannedAbsencesIcon.svg?react"
import StudentsIcon from "#assets/studentsIcon.svg?react"
import TryoutsIcon from "#assets/tryoutsIcon.svg?react"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import Counter from "#blocks/Counter"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import ClickableClassName from "#components/ClickableClassName"
import CopyClassContactInfo from "#components/CopyClassContactInfo"
import { ROOM_COLORS } from "#constants/classOptions"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { AdminViewClassSession } from "#hooks/newHooks/useAdminViewData"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import {
  AttendanceSummary,
  getSessionAttendanceSummary,
  summarizeSessionStudentsByType,
} from "#utils/classHelpers"
import { formatTimeLocale } from "#utils/dateHelpers"
import SessionProjectInfo from "#views/TeacherView/SessionProjectInfo"

const SCounter = ({ count }: { count: number }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        height: "16px",
        justifyContent: "flex-end",
      }}
    >
      <Counter value={count} />
    </span>
  )
}

const TooltipTagIconCounter = ({
  title,
  icon,
  color,
  count,
}: {
  title: string
  icon: React.ReactNode
  color: string
  count: number
}) => {
  const tagStyle = {
    boxShadow: `0px 1px 1px #00000033`,
    border: `1px solid #00000033`,
    backgroundColor: color,
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 12,
    fontWeight: 600,
    color: "white",
    width: 44,
    justifyContent: "space-between",
    alignItems: "center",
    gap: 4,
    margin: 4,
  }

  return (
    <Tooltip title={title}>
      <span>
        <Flex style={tagStyle}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 12,
            }}
          >
            {icon}
          </span>
          <SCounter count={count} />
        </Flex>
      </span>
    </Tooltip>
  )
}

export const IconsRow = ({
  isLoading = false,
  studentTypeSummary,
  attendanceSummary,
}: {
  isLoading: boolean
  studentTypeSummary: {
    students: number
    tryouts: number
    makeups: number
    plannedAbsences: number
  }
  attendanceSummary: AttendanceSummary
}) => {
  const { students, tryouts, makeups, plannedAbsences } = studentTypeSummary
  const { ENROLLED, TOTAL } = attendanceSummary
  const { iconColors, eric } = useThemeContext()
  const isGreen = !ENROLLED && !isLoading // FIX ME

  return (
    <Flex gap={8} style={{ alignItems: "center" }}>
      {isGreen ? (
        <TooltipTagIconCounter
          title={"🎉 All students accounted for!"}
          icon={
            <CheckCircleOutlined color={eric.green} style={{ fontSize: 12 }} />
          }
          color={eric.green}
          count={TOTAL}
        />
      ) : (
        <TooltipTagIconCounter
          title={"Some students haven't checked in yet!"}
          icon={<StudentsIcon />}
          color={eric.gray}
          count={TOTAL}
        />
      )}
      {">"}
      <Flex gap={0}>
        <TooltipTagIconCounter
          title="Students"
          icon={<UserOutlined style={{ fontSize: 11 }} />}
          color={students ? iconColors.students : iconColors.empty}
          count={students}
        />
        <TooltipTagIconCounter
          title="Tryouts"
          icon={<TryoutsIcon fill={iconColors.tryouts} />}
          color={tryouts ? iconColors.tryouts : iconColors.empty}
          count={tryouts}
        />
        <TooltipTagIconCounter
          title="Makeups"
          icon={<MakeupsIcon fill={iconColors.makeups} />}
          color={makeups ? iconColors.makeups : iconColors.empty}
          count={makeups}
        />
        <TooltipTagIconCounter
          title="Planned Absences"
          icon={<PlannedAbsencesIcon fill={iconColors.plannedAbsences} />}
          color={
            plannedAbsences ? iconColors.plannedAbsences : iconColors.empty
          }
          count={plannedAbsences}
        />
      </Flex>
    </Flex>
  )
}

const TEACHER_FONT_SIZE = [12, 10, 8, 6]
const TEACHER_MAX_WIDTH = 180

const AttendanceViewSessionSummary = ({
  adminViewSession,
}: {
  adminViewSession: AdminViewClassSession
}) => {
  const [isCardLoaded, setIsCardLoaded] = useState(false)
  const { session, room, project, notes } = adminViewSession
  const { white, border, altColor } = useThemeContext()
  const { canRead } = useResourcePermissions(RESOURCES.FRONT_DESK_VIEW)

  const { studentsByExceptionType, studentsById, teachersById, isReady } =
    useAdminViewContext()

  const handleCardLoaded = () => {
    setIsCardLoaded(true)
  }

  const attendanceSummary = getSessionAttendanceSummary(
    session,
    studentsById,
    studentsByExceptionType
  )

  const studentTypeSummary = summarizeSessionStudentsByType(
    session,
    session?.studentIds,
    studentsByExceptionType
  )

  const teachers = useMemo(() => {
    if (!isReady) return []

    return session.teacherIds
      ?.filter((id) => teachersById[id])
      ?.map((id) => teachersById[id].name)
      ?.map((name) => (
        <ComponentErrorBoundary
          key={`${name}-${session.id}`}
          variant="line"
          context="Teacher"
          fallbackStyle={{ maxWidth: TEACHER_MAX_WIDTH, color: white }}
        >
          <Flex grow style={{ maxWidth: TEACHER_MAX_WIDTH }}>
            <TextOverrun text={name} />
          </Flex>
        </ComponentErrorBoundary>
      ))
  }, [session.teacherIds, session.id, teachersById, isReady, white])

  // Scale the font size based on the number of teachers
  // Watch out for 0 length, DNE, etc.
  const teacherIndex = (session.teacherIds?.length || 1) - 1
  const teacherFontSize =
    TEACHER_FONT_SIZE[Math.min(teacherIndex, TEACHER_FONT_SIZE.length - 1)]

  return (
    <Flex
      style={{
        flexGrow: 1,
      }}
    >
      <AnimateInFlex
        col
        gap={12}
        style={{
          width: 350,
          height: 120,
          justifyContent: "space-between",
          borderRadius: 12,
          boxShadow: notes ? `0px 2px 2px #00000033` : `0px 2px 2px ${border}`,
          padding: "8px 12px 10px 16px",
          color: white,
          background: ROOM_COLORS[room],
          position: "relative",
        }}
        onAnimationComplete={handleCardLoaded}
        animation={ANIMATION_BY_KEY.leftSm}
        key={`${session.id}-${room}-${project?.id}-${session.startTime}-${session.endTime}`}
      >
        {notes ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 0,
              height: 0,
              borderTop: `16px solid ${altColor}`,
              borderLeft: "16px solid transparent",
              borderTopRightRadius: 4,
            }}
          />
        ) : null}
        <Flex style={{ position: "relative" }}>
          <Flex
            style={{
              flexWrap: "nowrap",
              fontSize: 18,
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            {formatTimeLocale(adminViewSession.startTime)} -{" "}
            {formatTimeLocale(adminViewSession.endTime)}
          </Flex>
          <Flex
            col
            style={{
              fontWeight: 600,
              position: "absolute",
              top: 30,
              width: 310,
              left: 0,
              fontSize: 18,
            }}
          >
            <ClickableClassName
              color={white}
              classId={session.classId}
              label={adminViewSession.name}
            />
            {/* <TextOverrun text={adminViewSession.name} /> */}
            <ComponentErrorBoundary
              variant="line"
              context="Project"
              fallbackStyle={{ color: white }}
            >
              <SessionProjectInfo session={session} project={project} inline />
            </ComponentErrorBoundary>
          </Flex>

          <Flex
            col
            style={{
              fontSize: teacherFontSize,
              marginLeft: "auto",
              alignItems: "flex-end",
              overflowY: "auto",
              height: 40,
              minWidth: 0,
              maxWidth: TEACHER_MAX_WIDTH,
              width: TEACHER_MAX_WIDTH,
            }}
          >
            {teachers}
          </Flex>
        </Flex>

        <Flex style={{ alignItems: "center" }} gap={12}>
          <IconsRow
            isLoading={!isReady && isCardLoaded}
            studentTypeSummary={studentTypeSummary}
            attendanceSummary={attendanceSummary}
          />
          {canRead && (
            <CopyClassContactInfo
              sessionId={session.id}
              className={adminViewSession.name}
            />
          )}
        </Flex>
      </AnimateInFlex>
    </Flex>
  )
}

export default AttendanceViewSessionSummary
